import { ChangeEvent, MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { bridgeAtom, useValue } from '@/store';
import {
  Arrow,
  DropdownList,
  DropdownListItem,
  DropdownValueContainer,
  DropdownWrapper,
  SelectInput,
  ValueText,
} from './Dropdown.styles';

interface DropdownProps {
  value?: string;
  index: number;
  values: string[];
  selectOption: (value: string, index: number) => void;
  bubbleRef: RefObject<HTMLDivElement | null>;
  labelText: string;
}

export const Dropdown = ({ values, value, index, selectOption, bubbleRef, labelText }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { translations } = useValue(bridgeAtom);

  const wrapRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const dropdownListRef = useRef<HTMLUListElement>(null);

  const onSelectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      selectOption(event.target.value, index);
      setIsOpen(false);
    },
    [index, selectOption]
  );

  const onListItemClick = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      selectOption(event.currentTarget.innerText, index);
      setIsOpen(false);
    },
    [index, selectOption]
  );

  useEffect(() => {
    const dropdownEl = dropdownListRef?.current;
    const bubbleEl = bubbleRef?.current;

    if (!isOpen || !dropdownEl || !bubbleEl) {
      return;
    }

    const bubbleRect = bubbleEl.getBoundingClientRect();
    const dropdownRect = dropdownEl.getBoundingClientRect();

    const spaceBelow = bubbleRect.bottom - dropdownRect.top - 10;

    dropdownEl.style.maxHeight = `${spaceBelow}px`;
  }, [isOpen, bubbleRef]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const shadowRoot = selectRef.current?.getRootNode() as ShadowRoot;

    const handleClickOutside = (event: Event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    shadowRoot.addEventListener('mousedown', handleClickOutside);

    return () => {
      shadowRoot.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <DropdownWrapper ref={wrapRef}>
      <DropdownValueContainer
        className="product-variants-dropdown-value"
        onClick={() => setIsOpen((prevState) => !prevState)}
      >
        <ValueText>
          {value
            ? `${labelText} - ${value}`
            : `${window.LLL(translations.variants.pleaseSelect)} ${labelText.toLowerCase()}`}
        </ValueText>
        <Arrow isOpen={isOpen} />
      </DropdownValueContainer>

      {isOpen && (
        <DropdownList className="product-variants-dropdown-list" ref={dropdownListRef}>
          {values.map((item) => (
            <DropdownListItem className="product-variants-dropdown-list-item" key={item} onClick={onListItemClick}>
              {item}
            </DropdownListItem>
          ))}
        </DropdownList>
      )}

      <SelectInput ref={selectRef} onChange={onSelectChange}>
        {values.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </SelectInput>
    </DropdownWrapper>
  );
};
